import { forwardRef } from "react";
import { cn } from "@/lib/utils";

type InfoCardPadding = "none" | "sm" | "md" | "lg";
type InfoCardGap = "none" | "sm" | "md" | "lg";

const paddingClasses: Record<InfoCardPadding, string> = {
  none: "p-0",
  sm: "p-4",
  md: "p-5",
  lg: "p-6",
};

const gapClasses: Record<InfoCardGap, string> = {
  none: "gap-0",
  sm: "gap-3",
  md: "gap-4",
  lg: "gap-5",
};

export interface InfoCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  withBackground?: boolean;
  withBorder?: boolean;
  withBlur?: boolean;
  padding?: InfoCardPadding;
  gap?: InfoCardGap;
  headerClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  actionsClassName?: string;
  contentClassName?: string;
}

export const InfoCard = forwardRef<HTMLDivElement, InfoCardProps>(
  (
    {
      title,
      description,
      actions,
      withBackground = true,
      withBorder = true,
      withBlur,
      padding = "md",
      gap = "lg",
      className,
      headerClassName,
      titleClassName,
      descriptionClassName,
      actionsClassName,
      contentClassName,
      children,
      ...props
    },
    ref,
  ) => {
    const shouldRenderHeader = title || description || actions;
    const resolvedBlur = withBlur ?? withBackground;

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col rounded-xl",
          gapClasses[gap],
          paddingClasses[padding],
          withBackground ? "bg-cede-bg-component" : "bg-transparent",
          withBorder ? "border border-cede-border-container" : "",
          resolvedBlur ? "backdrop-blur-xl" : "",
          className,
        )}
        {...props}
      >
        {shouldRenderHeader && (
          <div className={cn("flex flex-col gap-2 md:flex-row md:items-start md:justify-between", headerClassName)}>
            <div className="flex flex-col gap-1">
              {title && <h3 className={cn("text-lg font-semibold text-cede-text-primary", titleClassName)}>{title}</h3>}
              {description && (
                <p className={cn("text-sm text-cede-text-secondary", descriptionClassName)}>{description}</p>
              )}
            </div>
            {actions && <div className={cn("flex flex-wrap items-center gap-2", actionsClassName)}>{actions}</div>}
          </div>
        )}

        <div className={cn("flex flex-col gap-4", contentClassName)}>{children}</div>
      </div>
    );
  },
);

InfoCard.displayName = "InfoCard";
